<template>
	<JBox>
		<TinymceEditor :init="computedInit" class="tinymce-editor" :value="computedContent" @input="onInput($event)" />
		<!-- LiquidPicker -->
		<CModal variant="default-large" zIndex="10000" :isVisible="showLiquidPicker" @overlay:click="toggleLiquidPicker">
			<JBox class="s-bg-white s-p-2">
				<CLiquidPicker @data:selected="handleLiquidPicker" />
			</JBox>
		</CModal>
		<!-- FilePicker -->
		<CModal variant="default-large" zIndex="10000" :isVisible="showFilePicker" @overlay:click="toggleFilePicker">
			<CFilesExplorer :isFilePicker="true" @file:selected="handleFilePicker" />
		</CModal>
	</JBox>
</template>

<script>
// List of available plugins - https://www.tiny.cloud/docs/plugins
import "tinymce/tinymce"
import "tinymce/icons/default/icons"
import "tinymce/themes/silver/theme"
import "tinymce/plugins/paste/plugin"
import "tinymce/plugins/lists/plugin"
import "tinymce/plugins/advlist/plugin"
import "tinymce/plugins/table/plugin"
import "tinymce/plugins/code/plugin"
import "tinymce/plugins/link/plugin"
import "tinymce/plugins/image/plugin"
import "tinymce/plugins/pagebreak/plugin"
import "tinymce/plugins/searchreplace/plugin"
import "tinymce/plugins/hr/plugin"
import "tinymce/plugins/fullscreen/plugin"
import "tinymce/plugins/nonbreaking/plugin"

// skins
import "./skin.css"
import Editor from "@tinymce/tinymce-vue"

export default {
	components: {
		TinymceEditor: Editor
	},

	props: {
		content: null,
		hasMenuBar: {
			type: Boolean,
			default: () => true
		},
		toolbarConfig: {
			type: [Array, String],
			default: () => "default"
		},
		height: {
			type: Number
		},
		hasLiquidPicker: {
			type: Boolean,
			default: () => false
		},
		hasFilePicker: {
			type: Boolean,
			default: () => false
		}
	},

	data() {
		return {
			editor: null,
			timer: null,
			init: {
				selector: "#editor",
				skin: false,
				height: this.height,
				branding: false,
				menubar: "file edit view insert format tools table custom",
				plugins: [
					"lists",
					"advlist",
					"table",
					"code",
					"link",
					"image",
					"pagebreak",
					"hr",
					"searchreplace",
					"fullscreen",
					"nonbreaking"
				],
				toolbar: null,
				content_style: "@import url('https://fonts.googleapis.com/css2?family=Ubuntu:wght@300;400;500;700&display=swap');",
				font_formats:
					"Andale Mono=andale mono,times;Ubuntu=ubuntu; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats",
				valid_children: "+body[style]", // allow style tags in html
				forced_root_block: false,
				nonbreaking_force_tab: true,
				pagebreak_separator: "<div style='page-break-after: always;'></div>",
				file_picker_types: "image",
				menu: {
					custom: { title: "Advanced", items: "liquidPicker filePicker" }
				},
				setup: editor => {
					this.editor = editor
					if (this.hasLiquidPicker) {
						editor?.ui?.registry?.addMenuItem("liquidPicker", {
							text: "Liquid Picker",
							onAction: () => {
								this.toggleLiquidPicker()
							}
						})
					}
					if (this.hasFilePicker) {
						editor?.ui?.registry?.addMenuItem("filePicker", {
							text: "File Picker",
							onAction: () => {
								this.toggleFilePicker()
							}
						})
					}
				}
			},
			defaultToolBar: [
				"undo",
				"redo",
				"fontselect",
				"fontsizeselect",
				"bold",
				"italic",
				"alignleft",
				"aligncenter",
				"alignright",
				"alignjustify",
				"link",
				"image",
				"numlist",
				"bullist",
				"pagebreak",
				"hr",
				"searchreplace",
				"nonbreaking"
			],

			showLiquidPicker: false,
			showFilePicker: false,

			acceptedFileTypes: ["JPG", "JPEG", "PNG"]
		}
	},

	methods: {
		onInput($event) {
			this.$emit("input", $event)
		},
		decideToolBar() {
			//  default value
			if (this.toolbarConfig === "default") {
				return this.defaultToolBar?.join(" ")
			} else return this.toolbarConfig?.join(" ")
		},
		toggleLiquidPicker() {
			this.showLiquidPicker = !this.showLiquidPicker
		},
		toggleFilePicker() {
			this.showFilePicker = !this.showFilePicker
		},
		handleLiquidPicker(payload) {
			this.editor.insertContent(payload)
			this.toggleLiquidPicker()
		},
		handleFilePicker(obj) {
			const fileType = obj?.fileName
				?.split(".")
				?.pop()
				?.toUpperCase()
			// only allow certain filetypes to be added
			if (!this.acceptedFileTypes?.includes(fileType)) {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: {
						type: "message",
						text: this.$t("core.pleaseSelectImageFile")
					}
				})
				return
			}
			// we add img element here
			const encodedKey = encodeURIComponent(obj?.key)
			// CH-37826: must use the ABSOLUTE filesystem URL, never the same-origin rewrite.
			// This src is persisted into saved template HTML and re-rendered on other origins
			// (PDF service, client portals), where a relative /api/v1/files path is dead.
			const fileSystem = window.envConfig?.COVERGO_FILE_SYSTEM_DIRECT || window.envConfig?.COVERGO_FILE_SYSTEM
			const src = `${fileSystem}/${encodedKey}?tenantIdIfNoToken=${this.$store.state.tenantId}`
			this.editor.insertContent(`<img alt="${obj.name}" src="${src}"/>`)
			this.toggleFilePicker()
		}
	},

	computed: {
		computedContent() {
			return this.content
		},
		computedInit() {
			return {
				...this.init,
				toolbar: this.decideToolBar()
			}
		}
	}
}
</script>
